import { CacheExpiredError } from './cache-expired.error';
import { CacheKey } from './cache-key.enum';
import { CacheStorageType } from './cache-storage-type.enum';

const DEFAULT_TTL_SECONDS = 60;

/**
 * Modelo genérico de cache. Toda entidade cacheável cria uma classe própria
 * que estende `CacheEntry<T>` (ver `ThemesCache`/`GamesCache`), reexpondo
 * `create()`/`get()` como métodos públicos (widening de `protected` para
 * `public` — só a subclasse decide o que sai para fora).
 *
 * Construir o objeto (`new GamesCache(games)`) só monta o wrapper em
 * memória — a chave só passa a existir no Storage quando `create()` é
 * chamado. Isso também permite instanciar sem dado nenhum (`new
 * GamesCache()`) apenas para ler o cache existente com `get()`, facilitando
 * reaproveitar o conteúdo já salvo em vez de sempre acoplar leitura à
 * gravação.
 */
export abstract class CacheEntry<T> {
  protected readonly key: CacheKey;
  protected readonly type: CacheStorageType;
  protected readonly ttlSeconds: number;
  protected readonly data?: T;

  protected constructor(
    key: CacheKey,
    type: CacheStorageType,
    ttlSeconds: number = DEFAULT_TTL_SECONDS,
    data?: T,
  ) {
    this.key = key;
    this.type = type;
    this.ttlSeconds = ttlSeconds;
    this.data = data;
  }

  /** Serializa `data` com os metadados de expiração e grava no Storage certo — é aqui que a chave do cache é criada. */
  protected create(): void {
    const createdAt = new Date();
    const ttl = new Date(createdAt.getTime() + this.ttlSeconds * 1000);
    CacheEntry.storageFor(this.type).setItem(this.key, JSON.stringify({ createdAt, ttl, data: this.data }));
  }

  /**
   * Lê `key` no Storage correspondente a `type` e devolve só o `data`
   * salvo. Se não houver nada salvo ou o TTL já tiver passado, remove a
   * chave do Storage e lança `CacheExpiredError` em vez de devolver um
   * valor obsoleto.
   */
  protected get(): T {
    const storage = CacheEntry.storageFor(this.type);
    const raw = storage.getItem(this.key);

    if (raw) {
      const parsed = JSON.parse(raw) as { ttl: string; data: T };
      if (!CacheEntry.isExpired(parsed.ttl)) {
        return parsed.data;
      }
    }

    storage.removeItem(this.key);
    throw new CacheExpiredError(this.key);
  }

  private static isExpired(ttl: string): boolean {
    return new Date(ttl).getTime() <= Date.now();
  }

  private static storageFor(type: CacheStorageType): Storage {
    return type === CacheStorageType.Local ? localStorage : sessionStorage;
  }
}
